import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Ban, Check, Copy, History, KeyRound, Pencil, Play, Plus, RotateCcw, Send, Webhook } from 'lucide-react'
import { ErrorApi } from '@/api/errores'
import { DialogoConMotivo } from '@/consola/DialogoConMotivo'
import {
  useActivarSuscripcion,
  useActualizarSuscripcion,
  useCrearSuscripcion,
  useDesactivarSuscripcion,
  useDetalleEntrega,
  useEntregas,
  useProbarSuscripcion,
  useReenviarEntrega,
  useRotarSecreto,
  useSuscripciones,
} from '@/dominio/webhooks/useWebhooks'
import {
  NOMBRE_ESTADO_ENTREGA,
  NOMBRE_ORIGEN,
  NOMBRE_RESULTADO_INTENTO,
  type EstadoEntrega,
  type EventoDisponible,
  type EventoWebhook,
  type FiltroEntregas,
  type ResultadoPrueba,
  type Suscripcion,
  type SuscripcionConSecreto,
} from '@/dominio/webhooks/tipos'
import { formatearFechaHora } from '@/lib/fecha'
import { copiarAlPortapapeles } from '@/lib/portapapeles'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, CampoTexto, Selector } from '@/ui/CampoFormulario'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { PanelLateral } from '@/ui/PanelLateral'
import type { Tono } from '@/ui/tonos'
import { EncabezadoPantalla, Tarjeta } from './-pantalla'

/**
 * Administración → Webhooks (`docs/adr/0030`).
 *
 * Avisos firmados que la plataforma manda a otro sistema —una tienda, un Zap,
 * un ERP— cuando a un comprobante le pasa algo. La pantalla tiene que dejar
 * claras cuatro cosas:
 *
 *   **el secreto se ve una sola vez** — al crear la suscripción o al rotarlo
 *
 *   **un aceptado condicional llega como aceptado** — es válido (regla 9)
 *
 *   **lo que falla se reintenta solo** — durante unas 31 horas, y se ve aquí
 *   intento por intento, con el reenvío a mano
 *
 *   **las que creó un sistema las gestiona ese sistema** — aquí se ven, se
 *   prueban y se desactivan, pero no se les cambia la URL ni el secreto
 */
export const Route = createFileRoute('/webhooks')({
  component: PantallaWebhooks,
})

function mensaje(error: unknown): string {
  return error instanceof Error ? error.message : 'Vuelva a intentarlo.'
}

function PantallaWebhooks() {
  const lista = useSuscripciones()
  const [editando, setEditando] = useState<Suscripcion | 'nueva' | null>(null)
  const [conSecreto, setConSecreto] = useState<SuscripcionConSecreto | null>(null)
  const [desactivando, setDesactivando] = useState<Suscripcion | null>(null)
  const [historial, setHistorial] = useState<Suscripcion | null>(null)
  const [pruebas, setPruebas] = useState<Record<string, ResultadoPrueba>>({})

  const activar = useActivarSuscripcion()
  const rotar = useRotarSecreto()
  const probar = useProbarSuscripcion()

  const sinPermiso = lista.error instanceof ErrorApi && lista.error.clase === 'permiso'
  const datos = lista.data
  const suscripciones = datos?.suscripciones ?? []
  const eventos = datos?.eventos ?? []
  const th = 'h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600'

  return (
    <div className="flex max-w-[1100px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Webhooks"
        descripcion="Avisos firmados a otro sistema —una tienda, un Zap, un ERP— cuando a un comprobante le pasa algo: se emite, la DGII lo acepta o lo rechaza, se anula, o le llega uno de un proveedor."
        acciones={
          sinPermiso ? null : (
            <Boton variante="primario" icono={<Plus />} disabled={datos ? !datos.disponible : true} onClick={() => setEditando('nueva')}>
              Nueva suscripción
            </Boton>
          )
        }
      />

      {sinPermiso ? (
        <Banner tono="ambar" titulo="Solo un administrador gestiona los webhooks.">
          Una suscripción manda a otro sistema el estado y los montos de todos los comprobantes. Pídale a un administrador del contribuyente que la cree.
        </Banner>
      ) : null}

      {datos && !datos.disponible ? (
        <Banner tono="ambar" titulo="Los webhooks no están disponibles todavía.">
          Falta configurar en la plataforma la clave que cifra los secretos de las suscripciones. Avise al equipo de Agentical Group.
        </Banner>
      ) : null}

      {!sinPermiso ? (
        <Tarjeta titulo="Suscripciones" relleno={false}>
          {lista.isError ? (
            <div className="p-4">
              <Banner tono="rojo" titulo="No se pudieron cargar las suscripciones.">
                {mensaje(lista.error)}
              </Banner>
            </div>
          ) : !lista.isLoading && suscripciones.length === 0 ? (
            <EstadoVacio variante="primera-vez" mensaje="Todavía no hay suscripciones. Cree una para que otro sistema se entere de lo que pasa con sus comprobantes." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] border-collapse text-cuerpo" data-densidad="normal">
                <thead className="bg-gris-50">
                  <tr className="border-b border-gris-300 text-left">
                    <th scope="col" className={th}>
                      Destino
                    </th>
                    <th scope="col" className={`${th} w-[230px]`}>
                      Eventos
                    </th>
                    <th scope="col" className={`${th} w-[140px]`}>
                      Estado
                    </th>
                    <th scope="col" className={`${th} w-[150px] text-right`}>
                      Última entrega
                    </th>
                    <th scope="col" className={`${th} w-[300px] text-right`}>
                      <span className="solo-lectores">Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lista.isLoading ? (
                    <FilasEsqueleto filas={3} columnas={5} />
                  ) : (
                    suscripciones.map((s) => {
                      const prueba = pruebas[s.id]
                      const deSistema = s.autor === 'clave'
                      return (
                        <tr key={s.id} className="border-b border-gris-200 align-top last:border-b-0">
                          <td className="max-w-0 px-2 py-2">
                            <span className="mono block truncate text-gris-900" title={s.url}>
                              {s.url}
                            </span>
                            <span className="block truncate text-etiqueta text-gris-500">
                              {s.descripcion ?? 'Sin descripción'}
                              {deSistema ? ' · la creó un sistema con su clave de integración' : ''}
                              {s.rotacionHasta ? ` · secreto anterior válido hasta ${formatearFechaHora(s.rotacionHasta)}` : ''}
                            </span>
                            {prueba ? <ResultadoDePrueba resultado={prueba} /> : null}
                          </td>
                          <td className="px-2 py-2 text-etiqueta text-gris-700">
                            {s.eventos.map((e) => nombreEvento(eventos, e)).join(' · ')}
                          </td>
                          <td className="px-2 py-2">
                            <InsigniaEstado
                              tono={s.activa ? (s.fallidasSeguidas > 0 ? 'ambar' : 'verde') : 'neutral'}
                              aclaracion={s.activa && s.fallidasSeguidas > 0 ? `${s.fallidasSeguidas} fallidas seguidas` : undefined}
                              titulo={s.motivoDesactivacion ?? undefined}
                            >
                              {s.activa ? 'Activa' : 'Desactivada'}
                            </InsigniaEstado>
                          </td>
                          <td className="numero px-2 py-2 text-right text-gris-700">
                            {s.ultimaEntregaEl ? formatearFechaHora(s.ultimaEntregaEl) : 'Ninguna'}
                          </td>
                          <td className="px-2 py-2">
                            <span className="flex flex-wrap justify-end gap-1">
                              <Boton
                                tamano="menor"
                                variante="discreto"
                                icono={<Play />}
                                disabled={probar.isPending}
                                onClick={() =>
                                  probar.mutate(s.id, {
                                    onSuccess: (r) => setPruebas((p) => ({ ...p, [s.id]: r })),
                                  })
                                }
                              >
                                Probar
                              </Boton>
                              <Boton tamano="menor" variante="discreto" icono={<History />} onClick={() => setHistorial(s)}>
                                Historial
                              </Boton>
                              {!deSistema ? (
                                <>
                                  <Boton tamano="menor" variante="discreto" icono={<Pencil />} onClick={() => setEditando(s)}>
                                    Editar
                                  </Boton>
                                  <Boton
                                    tamano="menor"
                                    variante="discreto"
                                    icono={<KeyRound />}
                                    disabled={rotar.isPending}
                                    onClick={() => rotar.mutate(s.id, { onSuccess: setConSecreto })}
                                  >
                                    Rotar secreto
                                  </Boton>
                                </>
                              ) : null}
                              {s.activa ? (
                                <Boton tamano="menor" variante="discreto" icono={<Ban />} onClick={() => setDesactivando(s)}>
                                  Desactivar
                                </Boton>
                              ) : (
                                <Boton tamano="menor" variante="discreto" icono={<RotateCcw />} disabled={activar.isPending} onClick={() => activar.mutate(s.id)}>
                                  Activar
                                </Boton>
                              )}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          {probar.isError || rotar.isError || activar.isError ? (
            <div className="border-t border-gris-200 p-4">
              <Banner tono="rojo" titulo="No se pudo completar la acción.">
                {mensaje(probar.error ?? rotar.error ?? activar.error)}
              </Banner>
            </div>
          ) : null}
        </Tarjeta>
      ) : null}

      {!sinPermiso ? <ComoVerificar /> : null}

      {editando ? (
        <EditarSuscripcion
          suscripcion={editando === 'nueva' ? null : editando}
          eventos={eventos}
          onCerrar={() => setEditando(null)}
          onCreada={(creada) => {
            setEditando(null)
            setConSecreto(creada)
          }}
        />
      ) : null}

      {conSecreto ? <SecretoRecienCreado datos={conSecreto} onCerrar={() => setConSecreto(null)} /> : null}

      {desactivando ? <Desactivar suscripcion={desactivando} onCerrar={() => setDesactivando(null)} /> : null}

      {historial ? <Historial suscripcion={historial} eventos={eventos} onCerrar={() => setHistorial(null)} /> : null}
    </div>
  )
}

function nombreEvento(catalogo: EventoDisponible[], codigo: string): string {
  if (codigo === 'webhook.prueba') return 'Prueba'
  return catalogo.find((e) => e.codigo === codigo)?.nombre ?? codigo
}

function ResultadoDePrueba({ resultado }: { resultado: ResultadoPrueba }) {
  return (
    <span className={`mt-1 block text-etiqueta ${resultado.entregada ? 'text-verde' : 'text-rojo'}`}>
      Prueba: {resultado.entregada ? 'entregada' : NOMBRE_RESULTADO_INTENTO[resultado.resultado].toLowerCase()}
      {resultado.httpStatus ? ` · HTTP ${resultado.httpStatus}` : ''} · {resultado.duracionMs} ms
      {resultado.error ? ` · ${resultado.error}` : ''}
    </span>
  )
}

function EditarSuscripcion({
  suscripcion,
  eventos,
  onCerrar,
  onCreada,
}: {
  suscripcion: Suscripcion | null
  eventos: EventoDisponible[]
  onCerrar: () => void
  onCreada: (creada: SuscripcionConSecreto) => void
}) {
  const crear = useCrearSuscripcion()
  const actualizar = useActualizarSuscripcion()
  const [url, setUrl] = useState(suscripcion?.url ?? 'https://')
  const [descripcion, setDescripcion] = useState(suscripcion?.descripcion ?? '')
  const [elegidos, setElegidos] = useState<EventoWebhook[]>(suscripcion?.eventos ?? ['documento.emitido', 'documento.aceptado', 'documento.rechazado'])

  const urlLimpia = url.trim()
  // Solo la forma: lo que decide si la dirección vale es el servidor, que además mira a dónde resuelve.
  const valido = /^https:\/\/[^\s/]+\.[^\s/]+/i.test(urlLimpia) && elegidos.length > 0 && descripcion.trim().length <= 200
  const trabajando = crear.isPending || actualizar.isPending
  const error = crear.error ?? actualizar.error

  const alternar = (e: EventoWebhook) => setElegidos((actuales) => (actuales.includes(e) ? actuales.filter((x) => x !== e) : [...actuales, e]))

  const enviar = () => {
    const peticion = { url: urlLimpia, eventos: elegidos, descripcion: descripcion.trim() || null }
    if (suscripcion) {
      actualizar.mutate({ id: suscripcion.id, peticion }, { onSuccess: onCerrar })
    } else {
      crear.mutate(peticion, { onSuccess: onCreada })
    }
  }

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      titulo={suscripcion ? 'Editar la suscripción' : 'Nueva suscripción'}
      subtitulo="A dónde se avisa y de qué"
      ancho="normal"
      pie={
        <div className="flex justify-end gap-2">
          <Boton variante="discreto" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" icono={<Webhook />} disabled={!valido || trabajando} onClick={enviar}>
            {trabajando ? 'Guardando…' : suscripcion ? 'Guardar' : 'Crear suscripción'}
          </Boton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <CampoTexto
          etiqueta="URL"
          obligatorio
          maxLength={2048}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          ayuda="Solo https://, y una dirección de Internet: la plataforma no avisa a redes privadas."
        />
        <CampoTexto
          etiqueta="Descripción"
          maxLength={200}
          placeholder="Tienda en línea, ERP de contabilidad…"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          ayuda="Para qué es. Opcional."
        />
        <fieldset className="flex flex-col gap-2">
          <legend className="pb-1 text-etiqueta font-medium text-gris-600">Eventos</legend>
          {eventos.map((e) => (
            <label key={e.codigo} className="flex items-start gap-2 rounded-md border border-gris-300 p-2 text-cuerpo has-[:checked]:border-marca has-[:checked]:bg-marca-tenue">
              <input type="checkbox" className="mt-1" checked={elegidos.includes(e.codigo)} onChange={() => alternar(e.codigo)} />
              <span>
                <span className="block font-medium text-gris-900">{e.nombre}</span>
                <span className="text-etiqueta text-gris-600">{e.descripcion}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {error ? (
          <Banner tono="rojo" titulo="No se pudo guardar.">
            {mensaje(error)}
          </Banner>
        ) : null}
      </div>
    </PanelLateral>
  )
}

/**
 * El secreto recién creado o rotado. Vive solo en el estado de este panel: al
 * cerrarlo no queda en ninguna parte, ni en la caché ni en el navegador.
 */
function SecretoRecienCreado({ datos, onCerrar }: { datos: SuscripcionConSecreto; onCerrar: () => void }) {
  const [copiado, setCopiado] = useState(false)

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      titulo="Secreto de la suscripción"
      subtitulo={<span className="mono">{datos.suscripcion.url}</span>}
      pie={
        <Boton variante="primario" onClick={onCerrar}>
          Ya lo copié
        </Boton>
      }
    >
      <div className="flex flex-col gap-4">
        <Banner tono="ambar" titulo="Cópielo ahora: no se vuelve a mostrar.">
          Con él, su sistema comprueba que cada aviso viene de la plataforma. Si se pierde, se rota y se pega el nuevo.
          {datos.suscripcion.rotacionHasta
            ? ` El anterior sigue firmando hasta el ${formatearFechaHora(datos.suscripcion.rotacionHasta)}, para que cambie el suyo sin perder avisos.`
            : ''}
        </Banner>
        <div className="flex items-start gap-2 rounded-md border border-gris-300 bg-gris-50 p-3">
          <span className="mono min-w-0 flex-1 break-all whitespace-normal text-gris-900 select-all">{datos.secreto}</span>
          <button
            type="button"
            onClick={() => {
              void copiarAlPortapapeles(datos.secreto).then((exito) => {
                if (!exito) return
                setCopiado(true)
                setTimeout(() => setCopiado(false), 1500)
              })
            }}
            aria-label={copiado ? 'Secreto copiado' : 'Copiar el secreto'}
            className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-gris-600 hover:bg-gris-100 hover:text-gris-900"
          >
            {copiado ? <Check className="size-4 text-verde" aria-hidden="true" /> : <Copy className="size-4" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </PanelLateral>
  )
}

function Desactivar({ suscripcion, onCerrar }: { suscripcion: Suscripcion; onCerrar: () => void }) {
  const desactivar = useDesactivarSuscripcion()

  return (
    <DialogoConMotivo
      abierto
      onCancelar={onCerrar}
      onConfirmar={(motivo) => desactivar.mutate({ id: suscripcion.id, motivo }, { onSuccess: onCerrar })}
      titulo="Desactivar la suscripción"
      descripcion={
        <>
          <p>
            <span className="mono">{suscripcion.url}</span> deja de recibir avisos ahora. Lo que estaba pendiente de entregar se descarta: se puede
            reenviar a mano desde el historial.
          </p>
          <p>Se puede volver a activar cuando quiera.</p>
        </>
      }
      etiquetaConfirmar="Sí, desactivar"
      destructiva
      trabajando={desactivar.isPending}
      error={desactivar.isError ? { titulo: 'No se pudo desactivar.', contexto: mensaje(desactivar.error) } : null}
    />
  )
}

function tonoEntrega(estado: EstadoEntrega): Tono {
  switch (estado) {
    case 'entregada':
      return 'verde'
    case 'pendiente':
      return 'azul'
    case 'fallida':
      return 'rojo'
    default:
      return 'neutral'
  }
}

function Historial({ suscripcion, eventos, onCerrar }: { suscripcion: Suscripcion; eventos: EventoDisponible[]; onCerrar: () => void }) {
  const [filtro, setFiltro] = useState<FiltroEntregas>({ suscripcion: suscripcion.id })
  const [detalle, setDetalle] = useState<string | null>(null)
  const entregas = useEntregas(filtro)
  const filas = entregas.data?.pages.flatMap((p) => p.entregas) ?? []
  const th = 'h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600'

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      titulo="Historial de entregas"
      subtitulo={<span className="mono">{suscripcion.url}</span>}
      ancho="ancho"
      pie={
        <div className="flex justify-end">
          <Boton tamano="menor" variante="discreto" disabled={!entregas.hasNextPage || entregas.isFetchingNextPage} onClick={() => void entregas.fetchNextPage()}>
            {entregas.isFetchingNextPage ? 'Cargando…' : entregas.hasNextPage ? 'Cargar más' : 'No hay más'}
          </Boton>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <CampoFormulario etiqueta="Estado">
            {(a) => (
              <Selector {...a} value={filtro.estado ?? ''} onChange={(e) => setFiltro((f) => ({ ...f, estado: e.target.value as EstadoEntrega | '' }))}>
                <option value="">Todos</option>
                {(Object.keys(NOMBRE_ESTADO_ENTREGA) as EstadoEntrega[]).map((e) => (
                  <option key={e} value={e}>
                    {NOMBRE_ESTADO_ENTREGA[e]}
                  </option>
                ))}
              </Selector>
            )}
          </CampoFormulario>
          <CampoFormulario etiqueta="Evento">
            {(a) => (
              <Selector {...a} value={filtro.evento ?? ''} onChange={(e) => setFiltro((f) => ({ ...f, evento: e.target.value }))}>
                <option value="">Todos</option>
                {eventos.map((e) => (
                  <option key={e.codigo} value={e.codigo}>
                    {e.nombre}
                  </option>
                ))}
                <option value="webhook.prueba">Prueba</option>
              </Selector>
            )}
          </CampoFormulario>
        </div>

        {entregas.isError ? (
          <Banner tono="rojo" titulo="No se pudo cargar el historial.">
            {mensaje(entregas.error)}
          </Banner>
        ) : !entregas.isLoading && filas.length === 0 ? (
          <EstadoVacio variante="sin-resultados" mensaje="Ninguna entrega con esos filtros." />
        ) : (
          <div className="overflow-x-auto rounded-md border border-gris-200">
            <table className="w-full min-w-[720px] border-collapse text-cuerpo" data-densidad="compacta">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300 text-left">
                  <th scope="col" className={`${th} w-[140px]`}>
                    Cuándo
                  </th>
                  <th scope="col" className={th}>
                    Evento
                  </th>
                  <th scope="col" className={`${th} w-[90px]`}>
                    Origen
                  </th>
                  <th scope="col" className={`${th} w-[70px] text-right`}>
                    Intento
                  </th>
                  <th scope="col" className={`${th} w-[160px]`}>
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody>
                {entregas.isLoading ? (
                  <FilasEsqueleto filas={6} columnas={5} />
                ) : (
                  filas.map((e) => (
                    <tr key={e.id} className="cursor-pointer border-b border-gris-200 last:border-b-0 hover:bg-gris-50" onClick={() => setDetalle(e.id)}>
                      <td className="numero px-2 py-1 text-left text-gris-700">{formatearFechaHora(e.creadaEl)}</td>
                      <td className="max-w-0 px-2 py-1">
                        <span className="block truncate text-gris-900">{nombreEvento(eventos, e.evento)}</span>
                        <span className="mono block truncate text-etiqueta text-gris-500" title={e.ultimoError ?? undefined}>
                          {e.ultimoError ?? e.eventoId}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-gris-700">{NOMBRE_ORIGEN[e.origen]}</td>
                      <td className="numero px-2 py-1 text-right text-gris-700">{e.intento}</td>
                      <td className="px-2 py-1">
                        <InsigniaEstado
                          tono={tonoEntrega(e.estado)}
                          aclaracion={e.estado === 'pendiente' && e.proximoIntentoEl ? formatearFechaHora(e.proximoIntentoEl) : e.httpStatus ? `HTTP ${e.httpStatus}` : undefined}
                        >
                          {NOMBRE_ESTADO_ENTREGA[e.estado]}
                        </InsigniaEstado>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detalle ? <DetalleDeEntrega id={detalle} eventos={eventos} onCerrar={() => setDetalle(null)} /> : null}
    </PanelLateral>
  )
}

function DetalleDeEntrega({ id, eventos, onCerrar }: { id: string; eventos: EventoDisponible[]; onCerrar: () => void }) {
  const detalle = useDetalleEntrega(id)
  const reenviar = useReenviarEntrega()
  const d = detalle.data

  return (
    <PanelLateral
      abierto
      onCerrar={onCerrar}
      titulo={d ? nombreEvento(eventos, d.entrega.evento) : 'Entrega'}
      subtitulo={d ? <span className="mono">{d.entrega.eventoId}</span> : null}
      ancho="ancho"
      pie={
        <div className="flex items-center justify-between gap-2">
          <span className="text-etiqueta text-gris-600">
            {reenviar.isSuccess ? 'Reenvío programado: sale en unos segundos, con el mismo webhook-id.' : ' '}
          </span>
          <Boton variante="secundario" icono={<Send />} disabled={!d || reenviar.isPending || reenviar.isSuccess} onClick={() => reenviar.mutate(id)}>
            {reenviar.isPending ? 'Reenviando…' : 'Reenviar'}
          </Boton>
        </div>
      }
    >
      {detalle.isError ? (
        <Banner tono="rojo" titulo="No se pudo cargar la entrega.">
          {mensaje(detalle.error)}
        </Banner>
      ) : !d ? (
        <p className="text-etiqueta text-gris-500">Cargando…</p>
      ) : (
        <div className="flex flex-col gap-4">
          {reenviar.isError ? (
            <Banner tono="rojo" titulo="No se pudo reenviar.">
              {mensaje(reenviar.error)}
            </Banner>
          ) : null}
          <p className="text-cuerpo text-gris-700">
            Pasó el {formatearFechaHora(d.ocurridoEl)}. {NOMBRE_ORIGEN[d.entrega.origen]}, {NOMBRE_ESTADO_ENTREGA[d.entrega.estado].toLowerCase()}
            {d.entrega.entregadaEl ? ` el ${formatearFechaHora(d.entrega.entregadaEl)}` : ''}.
          </p>
          <div className="flex flex-col gap-1">
            <span className="text-etiqueta font-medium text-gris-600">Cuerpo, tal como se firma</span>
            <pre className="mono max-h-[260px] overflow-auto rounded-md border border-gris-300 bg-gris-50 p-3 text-etiqueta break-all whitespace-pre-wrap text-gris-900">
              {d.cuerpo}
            </pre>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-etiqueta font-medium text-gris-600">Intentos</span>
            {d.intentos.length === 0 ? (
              <p className="text-etiqueta text-gris-500">Todavía ninguno.</p>
            ) : (
              d.intentos.map((i) => (
                <div key={i.numero} className="rounded-md border border-gris-200 p-2">
                  <p className="text-cuerpo text-gris-900">
                    {i.numero}. {NOMBRE_RESULTADO_INTENTO[i.resultado]}
                    {i.httpStatus ? ` · HTTP ${i.httpStatus}` : ''} · {i.duracionMs} ms ·{' '}
                    <span className="text-gris-600">{formatearFechaHora(i.empezadoEl)}</span>
                  </p>
                  {i.error ? <p className="text-etiqueta text-gris-600">{i.error}</p> : null}
                  {i.respuestaTruncada ? (
                    <pre className="mono mt-1 max-h-[120px] overflow-auto rounded bg-gris-50 p-2 text-etiqueta break-all whitespace-pre-wrap text-gris-700">
                      {i.respuestaTruncada}
                    </pre>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </PanelLateral>
  )
}

function ComoVerificar() {
  return (
    <Tarjeta titulo="Cómo verificar un aviso">
      <ul className="flex list-disc flex-col gap-2 pl-5 text-cuerpo text-gris-700">
        <li>
          Cada aviso es un <code className="mono">POST</code> con JSON y tres cabeceras del estándar Standard Webhooks:{' '}
          <code className="mono">webhook-id</code>, <code className="mono">webhook-timestamp</code> y <code className="mono">webhook-signature</code>.
        </li>
        <li>
          La firma es un HMAC-SHA256 de <code className="mono">webhook-id.webhook-timestamp.cuerpo</code>, con los bytes del secreto (lo que va después de{' '}
          <code className="mono">whsec_</code>, decodificado de base64), sobre el cuerpo tal como llegó. Rechace las marcas de tiempo de más de cinco minutos.
        </li>
        <li>
          Use <code className="mono">webhook-id</code> para no procesar dos veces el mismo aviso: es el mismo en cada reintento y en cada reenvío.
        </li>
        <li>
          Un <strong className="font-semibold text-gris-900">aceptado condicional</strong> llega como <code className="mono">documento.aceptado</code> con{' '}
          <code className="mono">condicional: true</code>. Es válido: no le emita una nota de crédito.
        </li>
        <li>
          Conteste con cualquier 2xx en menos de 15 segundos. Si no, se reintenta durante unas 31 horas; un <code className="mono">410</code> desactiva la
          suscripción.
        </li>
      </ul>
    </Tarjeta>
  )
}
